import type { DownloadMode } from '../types'
import { resolvePicksApiBase } from './base'
import { responseRequestId, withRequestId } from './instrumentation'

interface ImportMetaWithEnv extends ImportMeta {
  env?: { VITE_API_BASE?: string; VITE_GOOGLE_CLIENT_ID?: string }
}

export const API_BASE =
  resolvePicksApiBase((import.meta as ImportMetaWithEnv).env?.VITE_API_BASE as string | undefined)

export const GOOGLE_CLIENT_ID =
  ((import.meta as ImportMetaWithEnv).env?.VITE_GOOGLE_CLIENT_ID as string | undefined) ??
  '173835119835-3pau1og6u09nrislvlutmb168mqknbv3.apps.googleusercontent.com'

const STUDIO_TOKEN_KEY = 'shotselect-studio-jwt-v1'
const STUDIO_EXPIRY_KEY = 'shotselect-studio-expiry-v1'

export type StudioProfileType = 'individual' | 'studio-owner' | 'team-member'
export type StudioDateFormat = 'month-day-year' | 'day-month-year' | 'iso'

export interface StudioUser {
  id: string
  email: string
  displayName: string | null
  avatarUrl: string | null
}

export interface StudioSummary {
  id: string
  name: string
  slug: string
  brandLogoUrl: string | null
  brandColor: string | null
  role?: string
}

export interface StudioProfile {
  type: StudioProfileType
  displayName: string
  studioName: string
  email: string
  phone: string
  website: string
  brandLogoUrl: string
  brandColor: string
  timezone: string
  dateFormat: StudioDateFormat
  defaultTtlDays: number
  defaultQuotaTargetMin: number
  defaultQuotaTargetMax: number
  defaultPicksEnabled: boolean
  defaultDownloadEnabled: DownloadMode
  reminderCadenceDays: number
  instagramUrl: string
  facebookUrl: string
}

export interface StudioSession {
  user: StudioUser
  activeStudio: StudioSummary | null
  studios?: StudioSummary[]
}

export interface StudioLoginResponse extends StudioSession {
  token: string
  expiresAt: number
  isNewUser: boolean
}

export interface StudioLink {
  id: string
  shareToken: string
  shareUrl: string
  eventTitle: string
  eventDateLabel: string | null
  clientName: string | null
  status: string
  expiresAt: number
  createdAt: number
  finalizedAt: number | null
  picksCompletedAt: number | null
  sneakPeekUrl: string | null
  picksEnabled: boolean
  downloadEnabled: DownloadMode
  storageBytes?: number
  photoCount?: number
  voterCount?: number
  syncedAt?: number
  openedCount?: number
  galleryViewCount?: number
  photoOpenCount?: number
  downloadCount?: number
  shareSheetCount?: number
  lastClientEventAt?: number | null
  clientEvents?: StudioClientEvent[]
}

export interface StudioClientEvent {
  id: number
  voterToken: string | null
  photoId: string | null
  eventType: string
  metadata: Record<string, unknown> | null
  ts: number
}

export interface StudioLinkDetails extends StudioLink {
  storageBytes: number
  photoCount: number
  voterCount: number
  syncedAt: number
  openedCount: number
  galleryViewCount: number
  reviewViewCount: number
  photoOpenCount: number
  downloadCount: number
  shareSheetCount: number
  picksSubmitCount: number
  lastClientEventAt: number | null
  photos: Array<{
    photoId: string
    position: number
    isRecommended: boolean
    category: string | null
    tier: string | null
    uploaded: boolean
    sneakPeek: boolean
  }>
  voters: Array<{
    voterToken: string
    displayName: string | null
    firstSeenAt: number
    lastSeenAt: number
  }>
  clientEvents: StudioClientEvent[]
}

export interface CreateStudioLinkBody {
  eventTitle: string
  eventDateLabel?: string
  clientName?: string
  quotaTargetMin?: number
  quotaTargetMax?: number
  ttlDays?: number
  idempotencyKey?: string
  picksEnabled?: boolean
  downloadEnabled?: DownloadMode
  photos: Array<{
    photoId: string
    position: number
    isRecommended?: boolean
    category?: string | null
  }>
}

export interface CreateStudioLinkResponse {
  linkId: string
  shareToken: string
  shareUrl: string
  uploads: Array<{
    photoId: string
    thumbKey: string
    displayKey: string
    zoomKey: string
  }>
}

export class StudioApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public limit?: number,
    public requestId?: string
  ) {
    super(`studio_api_error_${status}_${code}`)
  }
}

export function readStudioToken(): string | null {
  try {
    const token = localStorage.getItem(STUDIO_TOKEN_KEY)
    const expiresAt = Number(localStorage.getItem(STUDIO_EXPIRY_KEY) ?? '0')
    if (!token) return null
    if (expiresAt > 0 && expiresAt < Date.now()) {
      clearStudioToken()
      return null
    }
    return token
  } catch {
    return null
  }
}

export function writeStudioToken(token: string, expiresAt: number): void {
  try {
    localStorage.setItem(STUDIO_TOKEN_KEY, token)
    localStorage.setItem(STUDIO_EXPIRY_KEY, String(expiresAt))
  } catch {
    /* ignore */
  }
}

export function clearStudioToken(): void {
  try {
    localStorage.removeItem(STUDIO_TOKEN_KEY)
    localStorage.removeItem(STUDIO_EXPIRY_KEY)
  } catch {
    /* ignore */
  }
}

async function parseError(res: Response, fallbackRequestId: string): Promise<StudioApiError> {
  let body: { error?: string; limit?: number } | null = null
  try {
    body = (await res.json()) as { error?: string; limit?: number }
  } catch {
    /* ignore */
  }
  return new StudioApiError(
    res.status,
    body?.error ?? `http_${res.status}`,
    body?.limit,
    responseRequestId(res, fallbackRequestId)
  )
}

async function studioFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = readStudioToken()
  if (!token) throw new StudioApiError(401, 'missing_session')
  const { headers, requestId } = withRequestId(init.headers)
  headers.set('Authorization', `Bearer ${token}`)
  if (init.body && !headers.has('Content-Type') && typeof init.body === 'string') {
    headers.set('Content-Type', 'application/json')
  }
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers })
  if (!res.ok) throw await parseError(res, requestId)
  return (await res.json()) as T
}

/**
 * ShotSelect sign-in, step 1: mail a six-digit code to the studio's address.
 * (Google sign-in was retired — the email code IS the studio sign-in now.)
 */
export async function requestStudioEmailCode(email: string): Promise<{ ok: boolean }> {
  const { headers, requestId } = withRequestId({ 'Content-Type': 'application/json' })
  const res = await fetch(`${API_BASE}/auth/email/request`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ email }),
  })
  if (!res.ok) throw await parseError(res, requestId)
  return (await res.json()) as { ok: boolean }
}

/** ShotSelect sign-in, step 2: trade the emailed code for a studio session. */
export async function signInWithEmailCode(
  email: string,
  code: string,
  studioName?: string
): Promise<StudioLoginResponse> {
  const { headers, requestId } = withRequestId({ 'Content-Type': 'application/json' })
  const res = await fetch(`${API_BASE}/auth/email/verify`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ email, code, studioName }),
  })
  if (!res.ok) throw await parseError(res, requestId)
  const data = (await res.json()) as StudioLoginResponse
  writeStudioToken(data.token, data.expiresAt)
  return data
}

export async function fetchStudioMe(): Promise<StudioSession> {
  return studioFetch<StudioSession>('/auth/me')
}

export async function fetchStudioProfile(): Promise<StudioProfile> {
  const data = await studioFetch<{ profile: StudioProfile }>('/auth/profile')
  return data.profile
}

export async function saveStudioProfile(profile: StudioProfile): Promise<StudioProfile> {
  const data = await studioFetch<{ profile: StudioProfile }>('/auth/profile', {
    method: 'PUT',
    body: JSON.stringify(profile),
  })
  return data.profile
}

export async function logoutStudio(): Promise<void> {
  try {
    await studioFetch<{ ok: true }>('/auth/logout', { method: 'POST' })
  } finally {
    clearStudioToken()
  }
}

export async function listStudioLinks(): Promise<StudioLink[]> {
  const data = await studioFetch<{ links: StudioLink[] }>('/links?limit=200')
  return data.links
}

export async function fetchStudioLink(linkId: string): Promise<StudioLinkDetails> {
  return studioFetch<StudioLinkDetails>(`/links/${encodeURIComponent(linkId)}`)
}

export async function revokeStudioLink(linkId: string): Promise<{ ok: true; status: 'revoked' }> {
  return studioFetch<{ ok: true; status: 'revoked' }>(
    `/links/${encodeURIComponent(linkId)}/revoke`,
    { method: 'POST' }
  )
}

export async function deleteStudioLink(
  linkId: string
): Promise<{ ok: true; deletedObjects: number }> {
  return studioFetch<{ ok: true; deletedObjects: number }>(`/links/${encodeURIComponent(linkId)}`, {
    method: 'DELETE',
  })
}

export async function createStudioLink(
  body: CreateStudioLinkBody
): Promise<CreateStudioLinkResponse> {
  return studioFetch<CreateStudioLinkResponse>('/links', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function uploadStudioVariant(
  linkId: string,
  photoId: string,
  variant: 'thumb' | 'display' | 'zoom',
  blob: Blob
): Promise<void> {
  await studioFetch<{ ok: true }>(
    `/links/${encodeURIComponent(linkId)}/photos/${encodeURIComponent(photoId)}/${variant}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'image/webp' },
      body: blob,
    }
  )
}

export async function finalizeStudioLink(linkId: string): Promise<{ shareUrl: string | null }> {
  const data = await studioFetch<{ ok: true; sneakPeekUrl: string | null }>(
    `/links/${encodeURIComponent(linkId)}/finalize`,
    { method: 'POST' }
  )
  return { shareUrl: data.sneakPeekUrl }
}

export async function uploadStudioLogo(blob: Blob): Promise<{ brandLogoUrl: string }> {
  const formData = new FormData()
  formData.append('logo', blob)
  return studioFetch<{ brandLogoUrl: string }>('/auth/profile/logo', {
    method: 'POST',
    body: formData,
  })
}

export async function uploadStudioAvatar(blob: Blob): Promise<{ avatarUrl: string }> {
  const formData = new FormData()
  formData.append('avatar', blob)
  return studioFetch<{ avatarUrl: string }>('/auth/profile/avatar', {
    method: 'POST',
    body: formData,
  })
}
